import React, { useState, useEffect, useContext } from 'react';
import { Paper, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CustomOrderArrow from '../common/IconosOrdenacion';
import { useNavigate } from 'react-router-dom';
import AppContext from '../AppContext'; 
import { formatEuro } from '../../functions/formatNumber';
import { getColorForAmount } from '../../functions/colors';
import monthName from '../../functions/month';


const MonthlyBalanceListGlobal = ({ months, numAnys, defaultViewMode = 'estalvi' }) => {

  const [sortConfig, setSortConfig] = useState({ key: 'mes', direction: 'desc' });
  const [viewMode, setViewMode] = useState(defaultViewMode);

  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);
  const { ctxApp } = useContext(AppContext);

  const navigate = useNavigate();

  const listMonths = Object.values(months);

  // Función para manejar la ordenación
  const handleSort = (event, key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    event.stopPropagation();
  };

  const handleToggleViewMode = (e) => {
    setViewMode(prev => {
      if (prev === 'estalvi') return 'inversiones';
      if (prev === 'inversiones') return 'actual';
      return 'estalvi';
    });
    e.stopPropagation();
  };

  const gotoDetail= (mes) => {
    ctxApp.dashboardMensualTabSelected = undefined;
    navigate(`mes/${mes}`);
  }

  // Función para ordenar los balances según el criterio
  const sortedBalances = [...listMonths].sort((a, b) => {
    // Comprobar la clave de ordenación actual
    switch (sortConfig.key) {
      case 'mes':
        // Ordenar por el número de mes (mes.numero)
        return sortConfig.direction === 'asc'
          ? a.mes - b.mes
          : b.mes - a.mes;

      case 'numMoviments':
        const numMovsA = a.importAgregat.numMoviments / numAnys;
        const numMovsB = b.importAgregat.numMoviments / numAnys;
        // Ordenar por el número de movimientos (movimientos.num)
        return sortConfig.direction === 'asc'
          ? numMovsA - numMovsB
          : numMovsB - numMovsA;

      case 'ingressos':
        // Ordenar por ingresos (importes.ingresos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.mitjaIngressos - b.importAgregat.mitjaIngressos
          : b.importAgregat.mitjaIngressos - a.importAgregat.mitjaIngressos;

      case 'despeses':
        // Ordenar por gastos (importes.gastos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.mitjaDespeses - b.importAgregat.mitjaDespeses
          : b.importAgregat.mitjaDespeses - a.importAgregat.mitjaDespeses;

      case 'saldo':
        // Ordenar por saldo según el modo
        const getSaldoVal = (item) => {
          if (viewMode === 'estalvi') return item.importAgregat.mitjaSaldoEstalvi;
          if (viewMode === 'inversiones') return item.importAgregat.mitjaInversions;
          return item.importAgregat.mitjaSaldo;
        };
        return sortConfig.direction === 'asc'
          ? getSaldoVal(a) - getSaldoVal(b)
          : getSaldoVal(b) - getSaldoVal(a);

      default:
        return 0; // Si no hay un criterio de ordenación válido
  }
  });
  
  return (
    <Paper sx={{ padding: '20px' }}>
        {/* Cabecera del Grid */}
        <Grid container spacing={2} alignItems="center" sx={{ 
          marginBottom: 2, 
          fontWeight: 'bold', 
          backgroundColor: '#f0f0f0', // Fondo claro
          color: '#000', // Texto oscuro
          paddingTop: '2px', 
          paddingBottom: '2px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd' // Borde inferior
        }}>
          <Grid size={{xs: 12, sm: 4 }}>
            <Typography variant="body2">
              <span onClick={(e) => handleSort(e, 'mes')}>
                Mes {sortConfig.key === 'mes' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>{' '}
              <span style={{ fontSize: '0.8rem', color: 'gray' }} onClick={(e) => handleSort(e, 'numMoviments')}>
                (Mi.Moviments) {sortConfig.key === 'numMoviments' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>
            </Typography>
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
            {viewMode !== 'inversiones' && (
              <Typography variant="body2">
                <span onClick={(e) => handleSort(e, 'ingressos')}>
                  Mi.Ingressos {sortConfig.key === 'ingressos' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>{' / '}
                <span onClick={(e) => handleSort(e, 'despeses')}>
                  Mi.Despeses {sortConfig.key === 'despeses' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>
              </Typography>
            )}
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="row" alignItems="center" justifyContent="flex-end" 
            sx={{ gap: '1px' }}>
            <IconButton onClick={handleToggleViewMode} size="small" title="Canviar vista de saldo" sx={{ marginRight: '5px' }}>
              <SwapHorizIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Typography variant="body2" onClick={(e) => handleSort(e, 'saldo')}>
              {viewMode === 'estalvi' ? "Mi.Saldo Estalvi" : viewMode === 'inversiones' ? "Mi.Inversions" : "Mi.Saldo"} {sortConfig.key === 'saldo' && <CustomOrderArrow direction={sortConfig.direction}/> }
            </Typography>
          </Grid>
        </Grid>    
        
        {/* List Mesos */}
        { sortedBalances.map((value) =>  {
          const saldoVal = viewMode === 'estalvi' 
            ? value.importAgregat.mitjaSaldoEstalvi 
            : viewMode === 'inversiones' 
              ? value.importAgregat.mitjaInversions 
              : value.importAgregat.mitjaSaldo;

          const colorSaldo = getColorForAmount(saldoVal, viewMode === 'inversiones');

          const numMovs = value.importAgregat.numMoviments / numAnys;
          return (
            <Grid container 
              key={value.mes} 
              spacing={2} 
              alignItems="center" 
              onClick={()=>gotoDetail(value.mes)}
              sx={{ 
                marginBottom: 1,
                paddingLeft: '10px',
                paddingRight: '10px',
                transition: 'background-color 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Cambia el color de fondo al pasar el ratón
                },
            }}>

              {/* Columna izquierda: Número del mes, nombre y número de movimientos */}
              <Grid size={{xs: 12, sm: 4 }}>
                <Typography variant="body1">
                  <span >{value.mes}. {monthName(value.mes)}</span>{' '}
                  <span style={{ fontSize: '0.8rem', color: 'gray' }}>
                    ({numMovs})
                  </span>
                </Typography>
              </Grid>

              {/* Columna central: Importe ingresado y gastado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
                {viewMode !== 'inversiones' ? (
                  <Typography variant="body2">
                    <span style={{ color: 'green' }}>
                      {formatEuro(value.importAgregat.mitjaIngressos)}
                    </span>{' / '}
                    <span style={{ color: 'red' }}>
                      {formatEuro(value.importAgregat.mitjaDespeses)}
                    </span>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="gray" sx={{ fontStyle: 'italic' }}>
                    -
                  </Typography>
                )}            
              </Grid>

              {/* Columna derecha: Saldo del mes y saldo acumulado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="flex-end" 
                sx={{ gap: '1px' }}>
                <Typography variant="body1" fontWeight="bold" color={colorSaldo} >
                  {formatEuro(saldoVal)}
                </Typography>
              </Grid>
            </Grid>
          );
        }
      )}
    </Paper>
    );
};

export default MonthlyBalanceListGlobal;
